import { AppWindow } from '../../definitions/AppWindow';

declare let window: AppWindow;
export const DISMISS_NOTIFICATIONS_MODAL_ICON = '#dismiss-notifications-modal__icon';
export const NOTIFICATIONS_MOBILE_POPOVER = 'notifications-mobile-popover';
export const NOTIFICATIONS_POPOVER = 'notifications-popover';
export const DATA_MODAL_ID = 'data-modal-id';

export function initNotificationPopovers() {
  document.querySelector(DISMISS_NOTIFICATIONS_MODAL_ICON)?.addEventListener('click', closeNotificationsModal);
  document.querySelector(DISMISS_NOTIFICATIONS_MODAL_ICON)?.addEventListener('keyup', function (e: KeyboardEvent) {
    if (e.key === 'Enter') {
      closeNotificationsModal(e);
    }
  });
  initNotificationPopover(NOTIFICATIONS_MOBILE_POPOVER);
  initNotificationPopover(NOTIFICATIONS_POPOVER);
  initPopoverUIEvents();
  window.cwtUpdateTracking?.(document.getElementById(NOTIFICATIONS_MOBILE_POPOVER));
  window.cwtUpdateTracking?.(document.getElementById(NOTIFICATIONS_POPOVER));
}

function getScrollTarget(id, popoverBodyEl) {
  if (id === NOTIFICATIONS_MOBILE_POPOVER) {
    return document;
  } else {
    return popoverBodyEl;
  }
}

function eventListenerScroll({ id, popoverBodyEl, notificationPopover }) {
  const mobileDoc = document.documentElement;

  if (!notificationPopover || (id !== NOTIFICATIONS_MOBILE_POPOVER && !popoverBodyEl) || !mobileDoc) {
    return;
  }

  const scrollPos =
    id === NOTIFICATIONS_MOBILE_POPOVER
      ? (window.scrollY || mobileDoc.scrollTop) - (mobileDoc.clientTop || 0)
      : popoverBodyEl.scrollTop - (popoverBodyEl.clientTop || 0);
  const tabListShadow = notificationPopover.querySelector('.cw_tab-list-shadow') as HTMLElement | null;
  if (tabListShadow) {
    tabListShadow.style.display = scrollPos > 5 ? 'block' : 'none';
  }
}

export function initNotificationPopover(id) {
  const notificationPopover = document.getElementById(id);
  if (!notificationPopover) {
    console.info('Aborting notifications initialization because element with ID ' + id + ' was not found');
    return;
  }
  const popoverBodyEl = notificationPopover.querySelector('.cw_popover-body');
  const leftTab = notificationPopover.querySelector('#cw_tab-notifications');
  const rightTab = notificationPopover.querySelector('#cw_tab-messages');
  if (!popoverBodyEl || !leftTab || !rightTab) {
    console.info('Aborting notifications initialization because required elements were not found in #' + id);
    return;
  }

  /**
   * Event handler for clicking on notifications/messages tab
   */
  function onTabClick(event) {
    const selectedTabs = notificationPopover.querySelectorAll('.cw_tab_selected');
    const selectedPanels = notificationPopover.querySelectorAll('.cw_panel_selected');
    selectedTabs.forEach((selectedTab) => {
      selectedTab.classList.remove('cw_tab_selected');
    });
    selectedPanels.forEach((selectedPanel) => {
      selectedPanel.classList.remove('cw_panel_selected');
    });
    const tabId = event.target.id;
    if (tabId) {
      const tab = notificationPopover.querySelector(`#${tabId}`);
      tab.classList.add('cw_tab_selected');

      if (tab) {
        const panel = tab.getAttribute('data-panel');
        const panelToSelect = notificationPopover.querySelector(`#${panel}`);
        if (panelToSelect) {
          panelToSelect.classList.add('cw_panel_selected');
        }
      }
    }
  }
  leftTab.addEventListener('click', onTabClick, false);
  rightTab.addEventListener('click', onTabClick, false);

  /**
   *  Add shadow to tab list on scroll
   */
  function addEventListenerScroll() {
    eventListenerScroll({
      id,
      popoverBodyEl,
      notificationPopover,
    });
  }

  const scrollTarget = getScrollTarget(id, popoverBodyEl);
  if (scrollTarget) {
    scrollTarget.addEventListener('scroll', addEventListenerScroll);
  }

  checkNotificationContent(notificationPopover);
}

/**
 * @function - Checks to see if notifiations tab has content when doing autodisplay,
 * If tab doesn't have content but messages does, then it will autoswitch to messages tab
 */
export function checkNotificationContent(popoverEl) {
  const notificationTabCountEl = popoverEl.querySelector('#notificationCount');
  const notificationsTab = popoverEl.querySelector('#cw_tab-notifications');
  const notificationsPanel = popoverEl.querySelector('.cw_panel_notifications');
  const messagesCountEl = popoverEl.querySelector('#messageCount');
  const messagesTab = popoverEl.querySelector('#cw_tab-messages');
  const messagesPanel = popoverEl.querySelector('.cw_panel_messages');
  let notificationsCount = parseInt(notificationTabCountEl.textContent, 10);
  let messageCount = parseInt(messagesCountEl.textContent, 10);

  notificationsCount = isNaN(notificationsCount) ? 0 : notificationsCount;
  messageCount = isNaN(messageCount) ? 0 : messageCount;
  if (notificationsCount === 0 && messageCount > 0) {
    messagesTab.classList.add('cw_tab_selected');
    messagesPanel.classList.add('cw_panel_selected');
    notificationsTab.classList.remove('cw_tab_selected');
    notificationsPanel.classList.remove('cw_panel_selected');
  }
}

/**
 * @function - Adds click event listener to body
 *
 * Note: Uses "event delegation" to conserve memory and handle dynamic elements
 */
function closeNotificationsModal(e) {
  const modalId = e?.target?.getAttribute(DATA_MODAL_ID);
  document.querySelector('#cw_linkedaccount_coachtip')?.classList.remove('cw_linkedaccount_coachtip_hidden');
  window.CWHelpers.modal.close(modalId);
}

function attachEventListeners(element, closingElement) {
  const createEventListener = () =>
    function (e) {
      if (!e?.target?.querySelector('a')) {
        closingElement?.click();
      }
      e?.stopPropagation();
    };
  element?.addEventListener('focusout', createEventListener());
}

function initPopoverUIEvents() {
  const notificationCenterElements = [
    {
      closeItemSelector: '#header-notifications',
      tabItemSelector: '#notifications-popover #cw_tab-messages',
      listSelectors: [
        '#notifications-popover .cw_notification_new',
        '#notifications-popover #cw_panel-messages .cw_message_tab',
      ],
    },
    {
      closeItemSelector: DISMISS_NOTIFICATIONS_MODAL_ICON,
      tabItemSelector: '#notifications-mobile-popover #cw_tab-messages',
      listSelectors: [
        '#notifications-mobile-popover .cw_notification_new',
        '#notifications-mobile-popover #cw_panel-messages .cw_message_tab',
      ],
    },
  ];

  notificationCenterElements.forEach(function ({ closeItemSelector, tabItemSelector, listSelectors }) {
    const closingItem = document.querySelector(closeItemSelector);
    const tabItem = document.querySelector(tabItemSelector);

    listSelectors.forEach(function (selector) {
      const listElements = document.querySelectorAll(selector);
      const lastElement = listElements?.length > 0 ? listElements[listElements.length - 1] : tabItem;

      attachEventListeners(lastElement, closingItem);
    });
  });
}

function onKeyPressOpenMobilePopUp(e: KeyboardEvent, openMobilePopup) {
  if (e.key === 'Enter') {
    openMobilePopup(e.target);
  }
}

export default function initNotifications(win) {
  document.querySelector('#header-openNotifications')?.addEventListener('click', function (e) {
    openMobilePopup(e.target);
  });

  document.querySelector('#header-openNotifications')?.addEventListener('keyup', onKeyPressOpenNotifications);

  function onKeyPressOpenNotifications(e: KeyboardEvent) {
    onKeyPressOpenMobilePopUp(e, openMobilePopup);
  }

  function openMobilePopup(target) {
    const modalId = target.getAttribute(DATA_MODAL_ID);
    const modal = document.getElementById(modalId);
    document?.body?.appendChild(modal);
    win?.CWHelpers?.modal?.open(modalId);
    document.querySelector('#notifications-mobile').removeAttribute('data-cwt-load-off');
    document.querySelector('#cw_linkedaccount_coachtip')?.classList.add('cw_linkedaccount_coachtip_hidden');
    setTimeout(() => {
      // wait for animation
      window.dispatchEvent(new CustomEvent('CWNotificationsPopover'));
    }, 2000);
  }

  document.body.addEventListener('click', function (event) {
    const popoverTrigger = document.querySelector('.cw_notifications-mobile-trigger');
    const target = event.target as HTMLElement;
    const className = target.className;
    let modalId = null;
    const modal = null;

    if (className && className.indexOf) {
      if (className.indexOf('cw_notifications-mobile-trigger') !== -1) {
        openMobilePopup(event.target);
      }
      if (
        className.indexOf('vx_modal-dismiss_trigger') !== -1 ||
        className.indexOf('vx_modal-dismiss_x-trigger') !== -1
      ) {
        modalId = target.getAttribute(DATA_MODAL_ID);
        popoverTrigger.removeAttribute('data-popover-autodisplay');
        window.CWHelpers.modal.close(modalId);
      }
      if (className.indexOf('js_notificationDesktopBtn') >= 0) {
        document.querySelectorAll('.js_notificationCount').forEach(function removeBadge(badgeElem) {
          badgeElem.parentNode.removeChild(badgeElem);
        });
        document.querySelector('#notifications-popover')?.removeAttribute('data-cwt-load-off');
        window.dispatchEvent(new CustomEvent('CWNotificationsPopover'));
      }
    }
  });

  const desktopPopover = document.querySelector('#notifications-popover');

  // do not run load handler for client-side notifications
  if (desktopPopover?.getAttribute('data-client-side-notifications') === 'true') {
    return;
  }
  window.addEventListener('load', initNotificationPopovers);
}
